// ============================================================================
// Inbox widget data layer.
//
// Both the Finance Inbox and HR Inbox dashboard widgets need a small handful
// of role-scoped counts. We batch them into one hook per widget rather than
// folding them into the executive dashboard hook — the finance/HR widgets
// have their own lifecycle (they only mount for relevant roles, and they
// only want their own counts) and adding them to use-dashboard-data.ts
// would force every other widget to pay the latency.
//
// The hook runs the queries in parallel via Promise.all so the entire
// widget paints in one round-trip rather than waterfalling. Each individual
// count is wrapped so a single failure (e.g. a table that doesn't exist
// yet) returns null rather than wiping out the whole widget.
// ============================================================================

import { useEffect, useState } from 'react';
import { supabase } from '../../../shared/supabase';
import { errorMessage } from '../../../shared/errors';

// No valid certificate of insurance: either none was ever uploaded, or the one
// on file has lapsed. The states come from v_provider_compliance_status; the
// vocabulary itself lives in shared/coi-state.ts.
const NO_VALID_COI_STATES = ['No COI on file', 'Expired'];

export interface FinanceInboxCounts {
  // Completed projects awaiting invoicing. Surfaces Jose's "is this
  // billable yet?" queue. projects.stage live values are Draft/Completed —
  // Completed maps to "Ready to Invoice" until the workflow grows another
  // stage between Completed and invoiced.
  ready_to_invoice: number | null;
  // facility_tasks scoped to Finance category, assigned to the signed-in
  // employee, and still Open.
  open_finance_tasks: number | null;
  // Subs with live work at our facilities and NO valid certificate of
  // insurance — either none was ever uploaded, or the one on file has lapsed.
  // Read from v_provider_compliance_status (partner_documents), scoped to
  // Active providers with active_assignments > 0. Finance cares because a
  // missing COI blocks payment in some hospital systems.
  // ⚠️ Renamed from `expired_cois` 2026-08-24: the old key counted only
  // lapsed dates and was blind to the larger population that never uploaded
  // one at all, which no expiry-comparison detector can ever see.
  subs_no_valid_coi: number | null;
}

export interface HrInboxCounts {
  // Open hiring requests — status IN (Open, Interview, On_Hold) after the
  // 2026-05-28 7-stage overhaul (was Open + Not Covered).
  open_hiring_requests: number | null;
  // Same no-valid-COI signal as the finance widget — HR also acts on it
  // (chasing the sub for a new certificate).
  subs_no_valid_coi: number | null;
  // facility_tasks scoped to HR category, assigned to the signed-in
  // employee, and still Open.
  open_hr_tasks: number | null;
}

interface InboxState<T> {
  data: T | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

// Generic shape: takes a loader returning the count object, fires it once on
// mount + on every external refresh tick.
function useInbox<T>(
  enabled: boolean,
  loader: () => Promise<T>,
  deps: unknown[],
): InboxState<T> {
  const [data, setData] = useState<T | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(enabled);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!enabled) {
      setData(null);
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    loader()
      .then((result) => {
        if (!cancelled) {
          setData(result);
          setIsLoading(false);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('[inbox] load failed', err);
        setError(
          errorMessage(err, 'Failed to load inbox counts.'),
        );
        setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tick, enabled, ...deps]);

  return {
    data,
    isLoading,
    error,
    refetch: () => setTick((n) => n + 1),
  };
}

// Wraps each count query so an unavailable table (missing migration,
// permission-denied) downgrades to `null` instead of taking the widget
// down. Returning null lets the widget render the row as "—" or hide it.
//
// Typed as PromiseLike because the supabase-js builder is `Thenable` but
// not strictly Promise — `await` lifts it the same way.
type CountResult = { count: number | null; error: unknown };

async function safeCount(
  q: () => PromiseLike<CountResult>,
): Promise<number | null> {
  try {
    const res = await q();
    if (res.error) {
      console.warn('[inbox] count query suppressed', res.error);
      return null;
    }
    return res.count ?? 0;
  } catch (err) {
    console.warn('[inbox] count query threw', err);
    return null;
  }
}

export function useFinanceInbox(
  enabled: boolean,
  myEid: string | null,
): InboxState<FinanceInboxCounts> {
  return useInbox<FinanceInboxCounts>(
    enabled,
    async () => {
      const [readyToInvoice, openFinanceTasks, subsNoValidCoi] =
        await Promise.all([
          safeCount(() =>
            supabase
              .from('projects')
              .select('*', { count: 'exact', head: true })
              .eq('stage', 'Completed'),
          ),
          // Only count tasks if we know who the user is — without an EID
          // the "assigned to me" filter would silently match everyone.
          myEid
            ? safeCount(() =>
                supabase
                  .from('facility_tasks')
                  .select('*', { count: 'exact', head: true })
                  .eq('category', 'Finance')
                  .eq('status', 'Open')
                  .eq('assigned_to', myEid),
              )
            : Promise.resolve(0),
          safeCount(() =>
            supabase
              .from('v_provider_compliance_status')
              .select('*', { count: 'exact', head: true })
              .eq('provider_status', 'Active')
              .gt('active_assignments', 0)
              .in('coi_state', NO_VALID_COI_STATES),
          ),
        ]);

      return {
        ready_to_invoice: readyToInvoice,
        open_finance_tasks: openFinanceTasks,
        subs_no_valid_coi: subsNoValidCoi,
      };
    },
    [myEid],
  );
}

export function useHrInbox(
  enabled: boolean,
  myEid: string | null,
): InboxState<HrInboxCounts> {
  return useInbox<HrInboxCounts>(
    enabled,
    async () => {
      const [openHiring, subsNoValidCoi, openHrTasks] = await Promise.all([
        safeCount(() =>
          supabase
            .from('hiring_requests')
            .select('*', { count: 'exact', head: true })
            .in('status', ['Open', 'Interview', 'On_Hold']),
        ),
        safeCount(() =>
          supabase
            .from('v_provider_compliance_status')
            .select('*', { count: 'exact', head: true })
            .eq('provider_status', 'Active')
            .gt('active_assignments', 0)
            .in('coi_state', NO_VALID_COI_STATES),
        ),
        myEid
          ? safeCount(() =>
              supabase
                .from('facility_tasks')
                .select('*', { count: 'exact', head: true })
                .eq('category', 'HR')
                .eq('status', 'Open')
                .eq('assigned_to', myEid),
            )
          : Promise.resolve(0),
      ]);

      return {
        open_hiring_requests: openHiring,
        subs_no_valid_coi: subsNoValidCoi,
        open_hr_tasks: openHrTasks,
      };
    },
    [myEid],
  );
}
